import { TableActionCell } from '@wix/design-system';
import React from 'react';
import { CollectionState, FiltersMap } from '@wix/bex-core';
import { observer } from 'mobx-react-lite';
import { ItemActionsState } from '../../state';
import { CollectionActionModal } from '../CollectionActionModal';
import { ActionsCellProps } from '../ActionsCell';
import { useToolbarCollectionContext } from '../ToolbarCollectionContext';
import {
  createSecondaryActionCellPropWithBi,
  createPrimaryActionCellPropsWithBI,
  reportOnPopoverMenuShow,
} from './CollectionItemActionsHelper';

export interface CollectionItemActionsProps<
  T,
  F extends FiltersMap = FiltersMap,
> {
  collection: CollectionState<T, F>;
  state: ItemActionsState<T, F>;
  renderActionCell?: (state: ItemActionsState<T, F>) => ActionsCellProps;
}

function _CollectionItemActions<T, F extends FiltersMap = FiltersMap>({
  state,
  renderActionCell,
}: CollectionItemActionsProps<T, F>) {
  const { confirmModal } = state;
  const toolbarState = useToolbarCollectionContext();
  const { toolbarBIReporter } = toolbarState;
  const actionCellProps = renderActionCell?.(state) ?? {};
  const keyedItem = state.actionsCellAPI.keyedItem;
  const extensionMenuItems =
    toolbarState.extensionMenuItems?.useExtensionMenuItems?.({
      containerId: actionCellProps.containerId,
    });

  const secondaryActionCellPropsWithBI = createSecondaryActionCellPropWithBi({
    toolbarBIReporter,
    actionCellProps,
    keyedItem,
    extensionMenuItems,
  });

  const primaryActionCellPropsWithBI = createPrimaryActionCellPropsWithBI(
    toolbarBIReporter,
    actionCellProps,
    keyedItem,
  );

  return (
    <>
      {confirmModal.value && (
        <CollectionActionModal state={confirmModal} {...confirmModal.value} />
      )}
      <TableActionCell
        dataHook="collection-actions-cell"
        {...actionCellProps}
        popoverMenuProps={{
          appendTo: 'window',
          placement: 'auto',
          onShow: () =>
            reportOnPopoverMenuShow(
              toolbarBIReporter,
              actionCellProps,
              state.keyedItem,
            ),
          ...actionCellProps.popoverMenuProps,
        }}
        primaryAction={primaryActionCellPropsWithBI}
        secondaryActions={secondaryActionCellPropsWithBI}
      />
    </>
  );
}

export const CollectionItemActions = observer(_CollectionItemActions);
